// NodeMeasurementField.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import {
  formatMeasurementValue,
  normalizeMeasurementDefaultUnit,
  normalizeMeasurementValueForInput,
  normalizeMeasurementValueForStorage,
} from 'utilities/Measurement';

// CSS
import './NodeMeasurementField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * NodeMeasurementField
 *
 * Renders a decimal input paired with a unit selector for measurement fields.
 * In readOnly / previewMode: friendly label (e.g. "12 m").
 *
 * @param {object} field - Field definition containing unit options and default unit.
 * @param {object|null} value - Current measurement value object.
 * @param {function} onChange - Persists the next measurement value.
 * @param {boolean} [readOnly=false]
 * @param {boolean} [previewMode=false]
 *
 * @returns {ReactNode} The rendered measurement editor.
 */
const NodeMeasurementField = ({
  field,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const language = useCurrentLanguage();
  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- MEMOS ----- //

  const options = useMemo(() => (
    resolveEnumOptionsForField(field, language)
  ), [field, language]);

  const resolvedDefaultUnit = useMemo(() => (
    normalizeMeasurementDefaultUnit(field?.defaultUnit, options)
  ), [field?.defaultUnit, options]);

  const displayLabel = useMemo(() => {
    const formatted = formatMeasurementValue(value);
    if (!formatted) return '';

    // Prefer option label for the unit when available (same stored unit key).
    const normalized = normalizeMeasurementValueForStorage(value);
    if (!normalized?.unit) return formatted;

    const option = options.find((entry) => String(entry?.value || '') === normalized.unit);
    const unitLabel = option ? getOptionLabel(option, language) : normalized.unit;
    return unitLabel
      ? `${normalized.value} ${unitLabel}`
      : String(normalized.value);
  }, [language, options, value]);

  // ----- STATE ----- //

  const [draftValue, setDraftValue] = useState('');
  const [draftUnit, setDraftUnit] = useState(resolvedDefaultUnit);

  // ----- EFFECTS ----- //

  useEffect(() => {
    const nextInput = normalizeMeasurementValueForInput(value);
    setDraftValue(nextInput.value);
    setDraftUnit(nextInput.unit || resolvedDefaultUnit);
  }, [resolvedDefaultUnit, value]);

  // ----- UTILITIES ----- //

  const commitChange = (nextDraftValue = draftValue, nextDraftUnit = draftUnit) => {
    if (isReadOnly) return;
    const resolvedUnit = normalizeMeasurementDefaultUnit(nextDraftUnit || resolvedDefaultUnit, options);
    const nextValue = normalizeMeasurementValueForStorage({
      unit: resolvedUnit,
      value: nextDraftValue,
    });
    const currentValue = normalizeMeasurementValueForStorage(value);

    if (JSON.stringify(currentValue) === JSON.stringify(nextValue)) {
      return;
    }

    onChange?.(nextValue);
  };

  // ----- RENDER ----- //

  if (isReadOnly) {
    return (
      <div className="node-measurement-field-container is-readonly">
        <div className="node-measurement-field-readonly">
          <span className="node-measurement-field-readonly-icon">
            <Icon
              iconKey="straighten"
              color={theme.foregroundMuted}
              width={22}
              height={22}
            />
          </span>
          <span
            className="node-measurement-field-readonly-label"
            style={{ color: theme.foreground }}
          >
            {displayLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="node-measurement-field-container">

      <div className="node-measurement-field-inputs">
        <TextInput
          className="node-measurement-field-value"
          hybrid
          placeholder="Set value..."
          startIconKey="straighten"
          type="number"
          value={draftValue}
          onBlur={() => commitChange()}
          onChange={(event) => setDraftValue(event.target.value)}
        />

        <SelectInput
          className="node-measurement-field-unit"
          hybrid
          value={draftUnit}
          options={options.map((option) => ({
            value: option.value,
            label: getOptionLabel(option, language),
          }))}
          onChange={(event) => {
            const nextUnit = event.target.value;
            setDraftUnit(nextUnit);
            commitChange(draftValue, nextUnit);
          }}
        />
      </div>
    </div>
  );
};

NodeMeasurementField.displayName = 'NodeMeasurementField';

export default NodeMeasurementField;
